/* istanbul ignore file */

import React, { MouseEvent } from 'react';
import { VisibilityTracker, VisibilityTrackerContainer } from '@noths/polaris-client-components';
import {
  grid,
  maxWidthContainer,
  OutlineButton,
  SkeletonCard,
} from '@noths/polaris-client-ribbons-design-system';
import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import * as R from 'ramda';

import { Partner, Product, SimilarProduct } from 'src/types';
import { ContactPartnerModal } from '../components/ContactPartnerModal';
import * as copy from '../constants';
import { RelatedProductClickPayload } from '../modules';
import { ContactPartnerSubmitPayload } from '../modules/thunks';
import {
  FieldValidationErrorPayload,
  PARTNER_STOREFRONT_VISIBILITY_NAME,
  PartnerProductCardsVisiblePayload,
  PartnerStorefrontLinkClickPayload,
} from '../tracking/events';
import { ContactFormServerErrors } from '../types';
import { PartnerCard } from './PartnerCard';
import { PartnerProductCard } from './PartnerProductCard';
import * as styles from '../styles/PartnerStorefront.styles';

export const RELATED_PRICE = 'recommended-price';
export const RELATED_PREVIOUS_PRICE = 'recommended-previous-price';

const NUMBER_OF_PRODUCTS_TO_DISPLAY = 4;
const NUMBER_OF_PRODUCTS_PER_ROW = 2;

export interface PartnerStorefrontProps {
  contactFormServerErrors: ContactFormServerErrors;
  isContactPartnerModalOpen: boolean;
  messageSentSuccessfully: boolean;
  partner: Partner;
  product: Product;
  currency: CurrencyCode;
  similarProducts: SimilarProduct[] | null;
  closeContactPartnerModal: VoidFunction;
  onContactPartnerSubmit: (payload: ContactPartnerSubmitPayload) => void;
  onContactPartnerSubmitError: (payload: FieldValidationErrorPayload) => void;
  onPartnerStorefrontLinkClick: (payload: PartnerStorefrontLinkClickPayload) => void;
  onPartnerProductCardVisible: (payload: PartnerProductCardsVisiblePayload) => void;
  onRelatedProductClick: (payload: RelatedProductClickPayload) => void;
  openContactPartnerModal: VoidFunction;
}

export const PartnerStorefront = ({
  closeContactPartnerModal,
  contactFormServerErrors,
  isContactPartnerModalOpen,
  messageSentSuccessfully,
  product,
  similarProducts,
  currency,
  onContactPartnerSubmit,
  onContactPartnerSubmitError,
  onPartnerStorefrontLinkClick,
  onPartnerProductCardVisible,
  openContactPartnerModal,
  onRelatedProductClick,
}: PartnerStorefrontProps) => {
  const products = similarProducts?.slice(0, NUMBER_OF_PRODUCTS_TO_DISPLAY) ?? [];
  const isLoading = products !== null && products.length === 0;

  const handleRelatedProductClick = (
    e: MouseEvent<HTMLAnchorElement>,
    { code, href }: RelatedProductClickPayload,
  ) => {
    e.preventDefault();
    onRelatedProductClick({ code, href });
  };

  return (
    <>
      <VisibilityTrackerContainer sectionName={PARTNER_STOREFRONT_VISIBILITY_NAME}>
        <div css={[grid, maxWidthContainer, styles.storefrontGrid]}>
          <div
            css={styles.partner}
            data-is-multi-row={products.length > NUMBER_OF_PRODUCTS_PER_ROW}
          >
            <PartnerCard
              partner={product.partner}
              onContactSellerClick={openContactPartnerModal}
              onPartnerStorefrontLinkClick={onPartnerStorefrontLinkClick}
            />
          </div>
          <VisibilityTracker
            sectionName="Partner product cards"
            onVisible={() => onPartnerProductCardVisible({ products })}
          >
            <div
              css={styles.products}
              data-is-multi-row={isLoading || products.length > NUMBER_OF_PRODUCTS_PER_ROW}
              data-is-single-product={!isLoading && products.length === 1}
            >
              {isLoading
                ? R.range(0, NUMBER_OF_PRODUCTS_TO_DISPLAY).map((i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SkeletonCard
                      key={`skeleton-${i}`}
                      css={styles.skeletonLoaderWrapper}
                      imageStyle={styles.imageWrapper}
                    />
                  ))
                : products.map((relatedProduct) => (
                    <PartnerProductCard
                      product={relatedProduct}
                      currency={currency}
                      onRelatedProductClick={(e) =>
                        handleRelatedProductClick(e, {
                          code: relatedProduct.code,
                          href: relatedProduct.links[0].href || '',
                        })
                      }
                      key={product.code}
                    />
                  ))}
            </div>
          </VisibilityTracker>
          <div css={styles.visitShop} data-testid={`${copy.VISIT_SHOP_CLASSNAME}-sm`}>
            <OutlineButton
              href={product.partner.links?.[1]?.rel || ''}
              size="primary"
              className={copy.VISIT_SHOP_CLASSNAME}
            >
              {copy.VISIT_SHOP}
            </OutlineButton>
          </div>
        </div>
      </VisibilityTrackerContainer>
      <ContactPartnerModal
        isModalOpen={isContactPartnerModalOpen}
        onModalClose={closeContactPartnerModal}
        product={product}
        onSubmit={onContactPartnerSubmit}
        onSubmitError={onContactPartnerSubmitError}
        messageSentSuccessfully={messageSentSuccessfully}
        serverErrors={contactFormServerErrors}
      />
    </>
  );
};
